// Behaviour for the page-section blocks (the section*.cshtml partials).
//
// Everything is delegated from the document and keyed on data attributes, so it
// works for blocks rendered after load and is safe to initialise twice.

const MINUS = '−';

let initialised = false;

export function initSections() {
  if (initialised) return;
  initialised = true;

  document.addEventListener('click', onClick);
  document.addEventListener('submit', onSubmit);
}

function onClick(event: Event) {
  const target = event.target as Element | null;
  if (!target) return;

  const tab = target.closest<HTMLElement>('[data-pt-tabs] [role="tab"]');
  if (tab) return selectTab(tab);

  const trigger = target.closest<HTMLElement>('[data-pt-accordion] button[aria-expanded]');
  if (trigger) return toggleAccordion(trigger);

  const filter = target.closest<HTMLElement>('[data-pt-filter] [data-filter]');
  if (filter) return applyFilter(filter);
}

function onSubmit(event: Event) {
  const form = event.target as HTMLFormElement | null;
  if (!form || !(form instanceof HTMLFormElement)) return;

  if (form.matches('[data-pt-lead-form]')) return submitLead(event, form);
  if (form.matches('form[data-pt-newsletter]')) return submitNewsletter(event, form);
}

// ── Tabs ─────────────────────────────────────────────────────────────────────

function selectTab(tab: HTMLElement) {
  const root = tab.closest<HTMLElement>('[data-pt-tabs]');
  if (!root) return;

  const tabs = Array.from(root.querySelectorAll<HTMLElement>('[role="tab"]'));
  const panels = Array.from(root.querySelectorAll<HTMLElement>('[data-pt-panel]'));
  const index = tabs.indexOf(tab);

  tabs.forEach((item, i) => {
    const active = i === index;
    item.classList.toggle('active', active);
    item.setAttribute('aria-selected', String(active));
  });

  panels.forEach((panel, i) => {
    panel.hidden = i !== index;
  });
}

// ── Accordion ────────────────────────────────────────────────────────────────

function toggleAccordion(button: HTMLElement) {
  const open = button.getAttribute('aria-expanded') !== 'true';
  button.setAttribute('aria-expanded', String(open));

  const panel = button.nextElementSibling as HTMLElement | null;
  if (panel) panel.hidden = !open;

  const mark = button.querySelector('span');
  if (mark) mark.textContent = open ? MINUS : '+';
}

// ── Lead capture ─────────────────────────────────────────────────────────────

// A demo: the form validates natively, then swaps for the success panel. Nothing is sent.
function submitLead(event: Event, form: HTMLFormElement) {
  event.preventDefault();
  if (!form.checkValidity()) {
    form.reportValidity();
    return;
  }

  const root = form.closest<HTMLElement>('[data-pt-lead]');
  const success = root?.querySelector<HTMLElement>('[data-pt-lead-success]');

  form.hidden = true;
  if (success) {
    success.hidden = false;
    success.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
}

// ── Newsletter ───────────────────────────────────────────────────────────────

function submitNewsletter(event: Event, form: HTMLFormElement) {
  event.preventDefault();
  if (form.classList.contains('is-done')) return;

  const input = form.querySelector<HTMLInputElement>('input');
  const button = form.querySelector<HTMLButtonElement>('button[type="submit"], button:not([type])');

  if (input) {
    input.value = '';
    input.disabled = !button;
  }

  if (button) {
    button.textContent = 'Subscribed';
    button.disabled = true;
  }

  form.classList.add('is-done');
}

// ── List filter ──────────────────────────────────────────────────────────────

function applyFilter(button: HTMLElement) {
  const root = button.closest<HTMLElement>('[data-pt-filter]');
  if (!root) return;

  const value = button.dataset.filter ?? '*';

  root.querySelectorAll<HTMLElement>('[data-filter]').forEach(item => {
    item.classList.toggle('active', item === button);
  });

  root.querySelectorAll<HTMLElement>('[data-tag]').forEach(item => {
    item.hidden = value !== '*' && item.dataset.tag !== value;
  });
}
